import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Shield, Save, LoaderCircle } from "lucide-react";
import { usePermissionTemplates, usePermissionsCatalog, type AdminUserRow } from "@/lib/queries";
import { useUpdateUser, useSetUserPermissions } from "@/lib/mutations";
import { apiFetch } from "@/lib/api";
import { DEFAULT_USER_GRANTS } from "@emr/shared";
import { PermissionMatrix } from "./PermissionMatrix";
import { TemplateSelector } from "./TemplateSelector";
import { toast } from "sonner";

export function EditUserDialog({
  open,
  onOpenChange,
  user,
  isSelf,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  user: AdminUserRow | null;
  isSelf: boolean;
}) {
  const [fullName, setFullName] = useState("");
  const [role, setRole] = useState<"admin" | "user">("user");
  const [templateId, setTemplateId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [permsLoaded, setPermsLoaded] = useState(false);
  const [pendingTemplateApply, setPendingTemplateApply] = useState<{ id: string | null; ids: string[] } | null>(null);

  const { data: templatesData } = usePermissionTemplates();
  const { data: catalog } = usePermissionsCatalog();
  const updateUser = useUpdateUser(user?.id ?? "");
  const setPerms = useSetUserPermissions(user?.id ?? "");

  const templates = templatesData?.templates ?? [];

  useEffect(() => {
    if (!open || !user) return;
    setFullName(user.name);
    setRole(user.role);
    setTemplateId(null);
    setPendingTemplateApply(null);
    setSelectedIds(new Set());
    setPermsLoaded(false);
    if (!user.id) return;
    apiFetch<{ permissionIds: string[]; templateId: string | null }>(`/admin/users/${user.id}/permissions`)
      .then((res) => {
        setSelectedIds(new Set(res.permissionIds ?? []));
        if (res.templateId) setTemplateId(res.templateId);
      })
      .catch(() => { /* toast handled */ })
      .finally(() => setPermsLoaded(true));
  }, [open, user]);

  const isAdmin = role === "admin";

  const applyTemplate = (id: string | null, rawIds: string[]) => {
    // Same semantics as CreateUserDialog: "No template" resets to the
    // DEFAULT grants, not an empty selection.
    const ids = id === null ? DEFAULT_USER_GRANTS : rawIds;
    const same = ids.length === selectedIds.size && ids.every((k) => selectedIds.has(k));
    if (!same && selectedIds.size > 0) {
      setPendingTemplateApply({ id, ids });
      return;
    }
    commitTemplate(id, ids);
  };

  const commitTemplate = (id: string | null, ids: string[]) => {
    setTemplateId(id);
    setSelectedIds(new Set(ids));
    setPendingTemplateApply(null);
  };

  const submit = async () => {
    if (!user) return;
    // Race guard: if the user's current grants haven't loaded yet, saving
    // would replace-all with an empty set (wiping their permissions).
    if (!isAdmin && !permsLoaded) return;
    const updateBody: { fullName?: string; role?: "admin" | "user" } = {};
    if (fullName.trim() && fullName !== user.name) updateBody.fullName = fullName;
    if (!isSelf && role !== user.role) updateBody.role = role;
    try {
      if (Object.keys(updateBody).length > 0) {
        await updateUser.mutateAsync(updateBody);
      }
      if (!isAdmin) {
        await setPerms.mutateAsync({ permissionIds: Array.from(selectedIds), templateId: templateId ?? undefined });
      }
      onOpenChange(false);
    } catch { /* toast handled */ }
  };

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-maroon-700 dark:text-amber-500">Edit User</DialogTitle>
          <DialogDescription>{user.email}{isSelf && " (you)"}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div>
              <Label>Full name</Label>
              <Input value={fullName} onChange={(e) => setFullName(e.target.value)} />
            </div>
            <div>
              <Label>Role</Label>
              <Select value={role} onValueChange={(v) => setRole(v as "admin" | "user")} disabled={isSelf && user.role === "admin"}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">User</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
              {isSelf && user.role === "admin" && (
                <p className="mt-1 text-xs text-muted">You cannot demote yourself from admin.</p>
              )}
            </div>
          </div>

          {isAdmin ? (
            <Alert>
              <Shield className="h-4 w-4" />
              <AlertDescription>Admin has full access to every module. No template or permission matrix is required.</AlertDescription>
            </Alert>
          ) : (
            <>
              <TemplateSelector
                templates={templates}
                value={templateId ?? "none"}
                onChange={(id, ids) => applyTemplate(id, ids)}
              />
              <PermissionMatrix selectedIds={selectedIds} onChange={setSelectedIds} />
            </>
          )}
        </div>

        {pendingTemplateApply && (
          <Dialog open={!!pendingTemplateApply} onOpenChange={(v) => { if (!v) setPendingTemplateApply(null); }}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Replace current permissions?</DialogTitle>
                <DialogDescription>
                  You currently have {selectedIds.size} permission(s) selected. Applying the template will replace them with {pendingTemplateApply.ids.length} permission(s). Continue?
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setPendingTemplateApply(null)}>Cancel</Button>
                <Button className="bg-maroon-600 hover:bg-maroon-700 text-cream-50" onClick={() => commitTemplate(pendingTemplateApply.id, pendingTemplateApply.ids)}>
                  Replace
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={updateUser.isPending || setPerms.isPending || (!isAdmin && !permsLoaded)} className="bg-maroon-600 hover:bg-maroon-700 text-cream-50">
            {updateUser.isPending || setPerms.isPending ? <LoaderCircle size={14} className="mr-1 animate-spin" /> : <Save size={14} className="mr-1" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
